import { BellRing, CalendarClock, LogOut, MapPin, ShieldCheck, UserRound } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { Card, EmptyState } from "../../shared/ui/Card";
import { Button } from "../../shared/ui/Button";
import { PageSkeleton, StatusBadge } from "../../shared/ui/Status";
import { useGovernance, useGovernanceActions } from "./useGovernance";
import { useOrganization } from "../organization/useOrganization";
import { roleMeta } from "../../app/navigation";
export function RoleInboxPage() {
  const { session, signOut, mode } = useAuth();
  const governance = useGovernance();
  const organization = useOrganization();
  const actions = useGovernanceActions();
  if (!session || !governance.data || !organization.data) return <PageSkeleton />;
  const account = organization.data.accounts.find((item) => item.id === session.profile.id);
  const coach = account?.coachId ? organization.data.accounts.find((item) => item.id === account.coachId) : null;
  const items = governance.data.announcements;
  const notifications = governance.data.notifications
    .filter((item) => item.recipientId === session.profile.id)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return (
    <div className="page-stack">
      <header className="page-heading">
        <div>
          <span>账号与通知中心</span>
          <h1>我的</h1>
          <p>查看与你的角色和门店相关的公告、身份范围与账号安全信息。</p>
        </div>
        <StatusBadge tone="success">
          {session.profile.storeName ?? "总部"}
        </StatusBadge>
      </header>
      <Card className="profile-card">
        <span>{session.profile.name.slice(0, 1)}</span>
        <div>
          <strong>{session.profile.name}</strong>
          <p>
            {session.profile.phone} · {session.profile.role}
          </p>
        </div>
        <ShieldCheck />
      </Card>
      <div className="profile-security-grid">
        <Card><UserRound /><div><span>身份与权限</span><strong>{roleMeta[session.profile.role].label}</strong><p>{account?.jobTitle || "正式账号"} · 状态：{session.profile.status === "active" ? "正常" : session.profile.status}</p></div></Card>
        <Card><MapPin /><div><span>组织归属</span><strong>{session.profile.storeName ?? "公司总部"}</strong><p>{coach ? `主教练：${coach.name}` : account?.assignedMemberCount ? `服务会员：${account.assignedMemberCount}人` : "权限随组织档案自动生效"}</p></div></Card>
        <Card><CalendarClock /><div><span>登录安全</span><strong>{mode === "supabase" ? "手机号 + 密码登录" : "演示认证模式"}</strong><p>{session.expiresAt ? `会话有效至 ${new Date(session.expiresAt * 1000).toLocaleString("zh-CN")}` : "当前会话由本机演示环境管理"}</p></div></Card>
      </div>
      {account?.role === "member" && <Card className="account-detail-card"><div><span>会员资料</span><strong>{account.gender === "male" ? "男" : account.gender === "female" ? "女" : "未公开"} · {account.birthday || "生日未填写"}</strong></div><StatusBadge tone={account.memberStatus === "active" ? "success" : account.memberStatus === "expiring" ? "warning" : "danger"}>{account.memberStatus === "active" ? "有效会员" : account.memberStatus === "expiring" ? "即将到期" : account.memberStatus === "paused" ? "暂停训练" : "已到期"}</StatusBadge></Card>}
      {(account?.role === "employee" || account?.role === "manager") && <Card className="account-detail-card"><div><span>员工资料</span><strong>{account.jobTitle || "岗位未填写"} · {account.birthday || "生日未填写"}</strong></div><StatusBadge tone="success">组织档案</StatusBadge></Card>}
      <Card className="account-actions-card"><div><strong>退出当前账号</strong><p>退出后需要重新输入手机号和密码。公共设备使用后请及时退出。</p></div><Button variant="ghost" onClick={() => signOut()}><LogOut size={15} />安全退出</Button></Card>
      <div className="section-divider">
        <span>INBOX</span>
        <h2>业务通知</h2>
      </div>
      {notifications.length ? (
        <div className="announcement-list">
          {notifications.map((item) => (
            <Card key={item.id}>
              <span>
                <BellRing />
              </span>
              <div>
                <small>
                  {item.readAt ? "已读" : "未读"} ·{" "}
                  {new Date(item.createdAt).toLocaleString("zh-CN")}
                </small>
                <h3>{item.title}</h3>
                <p>{item.content}</p>
              </div>
              {!item.readAt && (
                <Button
                  variant="ghost"
                  disabled={actions.pending}
                  onClick={() => actions.markNotificationRead(item.id)}
                >
                  标为已读
                </Button>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          title="暂无业务通知"
          description="排课、反馈、星能、退款和商城状态变化会显示在这里。"
        />
      )}
      <div className="section-divider">
        <span>NOTICES</span>
        <h2>公告与提醒</h2>
      </div>
      {items.length ? (
        <div className="announcement-list">
          {items.map((x) => (
            <Card key={x.id}>
              <span>
                <BellRing />
              </span>
              <div>
                <small>
                  {x.scope === "company" ? "总部公告" : "门店公告"} ·{" "}
                  {new Date(x.publishedAt).toLocaleDateString("zh-CN")}
                </small>
                <h3>{x.title}</h3>
                <p>{x.content}</p>
              </div>
              {x.scope === "store" && <MapPin />}
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          title="暂无公告"
          description="与你角色和门店相关的公告会显示在这里。"
        />
      )}
    </div>
  );
}
